import React, { useState, FC } from 'react'
import { Check2 } from 'react-bootstrap-icons'
import Button from '../atoms/Button/ButtonV2'
import IconPlay from '@/components/IconPlay'
import Modal from '@/components/Modal'
import Dialog from '@/components/Dialog'
import VideoPlayer from '@/components/VideoPlayer'
import { videoParams } from '@/components/organisms/VideoCards/VideoCards.jsx'
import { Department, getDepartmentText } from '@/domain/department'
import IconRightArrow from '@/components/IconRightArrow'
import useSegment from '@/hooks/segment/useSegment'
import useIsMobile from '@/hooks/useIsMobile'

const DEPARTMENT_TEXT = {
  [Department.Default]: {
    header: 'A Trusted Recovery Leader for Over 10 Years',
    body: 'Evidence and ethics matter to us. Since 2013, our expert team has built comprehensive resources you can trust to find the right treatment for you or your loved one.',
    features: [
      '10+ Years of Trusted Research',
      'Over 4 Million Users',
      'No Hotline or Referral Fees'
    ],
    button: {
      text: 'Start Your Journey',
      href: '/about/'
    }
  },
  [Department.Providers]: {
    header: "Find The Right Provider for Your Patient's Unique Situation",
    body: 'Thousands of healthcare professionals use Recovery.com every month to help their patients find the best treatment for them. We put everything you need to make treatment referrals at your fingertips.',
    features: [
      'Filter by zipcode, insurance, and conditions',
      'Narrow by level of care, amenities, and reviews',
      'Contact admissions teams directly'
    ],
    button: {
      text: 'Schedule a Demo',
      href: 'https://rehabpath.typeform.com/to/lthBKRoD',
      target: '_blank'
    }
  }
}

type FeatureProps = {
  title: string
}

const Feature: FC<FeatureProps> = ({ title }) => (
  <div className="flex gap-4">
    <div className="flex size-6 items-center justify-center rounded-full bg-primary-50">
      <Check2 className="size-4 text-gray-1100" />
    </div>
    <span className="font-medium text-gray-1100">{title}</span>
  </div>
)

const ATrustedRecoveryLeader: FC<{ department: Department }> = ({
  department
}) => {
  const [showVideo, setShowVideo] = useState(false)
  const segment = useSegment()
  const isMobile = useIsMobile()

  const isDepartmentDefault =
    department === 'default' || department === undefined

  const toggleVideo = () => {
    if (!isDepartmentDefault) {
      segment.video.proPageClick({
        page: window.location.pathname,
        platform: isMobile ? 'mobile' : 'desktop'
      })
    }

    setShowVideo(!showVideo)
  }

  const { header, body, features, button } = getDepartmentText({
    textValues: DEPARTMENT_TEXT,
    department
  })

  const imageSrc = isDepartmentDefault
    ? 'https://res.cloudinary.com/rehabpath/image/upload/c_lfill,q_60,f_auto,w_900,ar_3:2/v1712605535/recovery/9452a2072e4ea032b5ffe0a9a0cf06bf.png'
    : 'https://res.cloudinary.com/rehabpath/image/upload/v1748019684/assets/video/thumbnails/pro-referer-demo-thumbnail.gif'

  const altText = isDepartmentDefault
    ? 'About Recovery.com Team Photo'
    : 'Recovery.com Product Demo'

  return (
    <div className="flex flex-col items-center justify-center gap-10 xl:flex-row xl:gap-16">
      <div className="order-2 flex w-full flex-col gap-8 lg:order-1 xl:max-w-[440px]">
        <div className="flex flex-col items-start justify-center gap-6">
          <div className="flex flex-col gap-2">
            <p className="font-FieldsVariable text-2xl font-medium text-gray-1100 xl:max-w-[296px]">
              {header}
            </p>
          </div>
          <p className="text-lg text-gray-700">{body}</p>
          <div className="flex flex-col gap-4 xl:flex-col">
            {features.map((feature) => (
              <Feature
                key={feature}
                title={feature}
              />
            ))}
          </div>
        </div>
        <div>
          <Button
            type="secondary"
            as="a"
            href={button?.href}
            target={button?.target}
            className="inline-block w-full md:w-auto"
          >
            {button?.text}
            {button?.target === '_blank' && <IconRightArrow />}
          </Button>
        </div>
      </div>
      <div
        className="relative order-1 w-full cursor-pointer xl:order-2 xl:min-w-[732px]"
        onClick={toggleVideo}
      >
        <img
          className="aspect-video w-full rounded-2xl object-cover xl:max-w-[732]"
          src={imageSrc}
          alt={altText}
        />
        {isDepartmentDefault ? (
          <>
            <div className="absolute left-0 top-0 size-full rounded-2xl bg-[linear-gradient(180deg,rgba(59,47,147,0.0)_0%,rgba(46,37,115,0.2)_100%)]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <IconPlay customCss="w-12 md:w-20 text-gray-50 drop-shadow-[0_0_43px_rgba(0,0,0,0.3)]" />
            </div>
            <Modal
              isVisible={showVideo}
              setIsVisible={setShowVideo}
            >
              {showVideo && (
                <iframe
                  className="rounded-child block aspect-video w-full"
                  id="ytplayer"
                  src={`https://www.youtube.com/embed/IOvM9BFFVhM?${videoParams}`}
                  allowFullScreen
                  allow="autoplay"
                />
              )}
            </Modal>
          </>
        ) : (
          <>
            <div className="absolute left-0 top-0 size-full rounded-2xl bg-gray-500/20" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500/75 transition-transform hover:scale-110">
              <IconPlay customCss="w-12 md:w-20 text-gray-50 drop-shadow-[0_0_43px_rgba(0,0,0,0.3)]" />
            </div>
            <Dialog
              isOpen={showVideo}
              onOpenChange={setShowVideo}
            >
              <VideoPlayer url="https://res.cloudinary.com/rehabpath/video/upload/v1748012350/assets/video/pro-referer-demo.mp4" />
            </Dialog>
          </>
        )}
      </div>
    </div>
  )
}

export default ATrustedRecoveryLeader
